import { computed, inject, Injectable, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { PhoneNumberUtil } from '@ecm/material';
import {
    CarrouselItem,
    Contract,
    Family,
    Member,
    OptionCode,
    Portfolio,
    Usage,
    UsageNameToCodeEnum,
} from '@ecm/portfolio';
import { Link, LinkService } from '@ecm/router';

import { CARROUSEL_CURRENT_ITEM_KEY } from '../constants/carrousel-constant';
import {
    DEFAULT_CONTRACT,
    FIXE,
    MOBICARTE,
    OPEN_CARAIBE,
    ORANGE_BUSINESS_CONTRACT,
    ORANGE_PRO_CONTRACT,
    PARNASSE_CONTRACT,
    PROTECTED_HOUSE_CONTRACT,
    PROTECTED_HOUSE_CONTRACT_2,
} from '../constants/contract-type.constant';
import { DisplayOrder } from '../constants/equipments-display-order';
import { TrackedNames, TrackedZones } from '../constants/tracking.constant';
import { CarrouselItemButtonViewModel } from '../model/carrousel-item-button.view-model';
import { CarrouselItemViewModel } from '../model/carrousel-item.view-model';
import { CarrouselNavigationItemViewModel } from '../model/carrousel-navigation-item.view-model';
import { CarrouselOfferItemViewModel } from '../model/carrousel-offer-item.view-model';

@Injectable({
    providedIn: 'root',
})
export class CarrouselItemViewModelMapper {
    public carrouselTranslate: any;
    public carrouselTranslatelinks: any;
    public carrouselTranslateNavigation: any;
    public carrouselDetailsTranslate: any;

    private readonly translate = inject(TranslateService);
    private readonly linkService = inject(LinkService);

    public constructor() {
        this.carrouselTranslate = this.translate.instant('carrousel');
        this.carrouselDetailsTranslate = this.translate.instant('details');
    }

    public getCarrouselItems(portfolio: Portfolio): CarrouselItemViewModel[] {
        const carrouselItems: CarrouselItemViewModel[] = [];
        const offersItems: CarrouselOfferItemViewModel[] = [];
        if (portfolio?.carrousel) {
            for (const carrouselItem of portfolio.carrousel) {
                if (carrouselItem.memberId) {
                    carrouselItems.push(
                        this.getCarrouselMemberItemViewModel(carrouselItem, portfolio.families, portfolio.contracts),
                    );
                } else if (carrouselItem.contractId) {
                    carrouselItems.push(this.getCarrouselContractItemViewModel(carrouselItem, portfolio.contracts));
                } else if (carrouselItem.type === 'sharedServices') {
                    const offerItem = this.getCarrouselNavigations(null, true);
                    offersItems.push(offerItem);
                    const sharedServicesitem: CarrouselItemViewModel = {
                        id: 'SHAREDSERVICES',
                        avatarSrc: this.carrouselTranslate.default.avatarSrc,
                        avatarAlt: this.carrouselTranslate.default.avatarAlt,
                        button: this.extractButton('sharedServices', carrouselItem.title, TrackedNames.SHARED_SERVICES),
                        offerItems: offersItems,
                    };
                    carrouselItems.push(sharedServicesitem);
                }
            }
        }
        // For now, always append the AddMember carrousel item
        carrouselItems.push(this.getCarrouselAddMemberItemViewModel());
        return carrouselItems;
    }

    private getCarrouselMemberItemViewModel(
        memberItem: CarrouselItem,
        families: Family[],
        contracts: Contract[],
    ): CarrouselItemViewModel {
        const carrouselItemViewModel = {} as CarrouselItemViewModel;
        carrouselItemViewModel.offerItems = [];

        if (!families?.length) {
            return carrouselItemViewModel;
        }

        const matchingMember = families[0].members.find((member: Member) => member.id === memberItem.memberId);
        if (!matchingMember) {
            return carrouselItemViewModel;
        }

        const { avatarSrc, avatarAlt } = this.getMemberAvatarInfo(matchingMember);

        carrouselItemViewModel.id = memberItem.memberId;
        carrouselItemViewModel.type = memberItem.type;
        carrouselItemViewModel.avatarAlt = avatarAlt;
        carrouselItemViewModel.avatarSrc = avatarSrc;
        carrouselItemViewModel.button = this.extractButton(memberItem.memberId, memberItem.title, TrackedNames.MEMBER);

        const offersItems = this.buildOfferItemsForMember(matchingMember, contracts);
        carrouselItemViewModel.offerItems = this.checkDisplayOffersItems(offersItems);

        return carrouselItemViewModel;
    }

    private getMemberAvatarInfo(member: Member): { avatarSrc: string; avatarAlt: string } {
        const defaultAvatar = this.carrouselTranslate.default;

        if (member?.avatar) {
            return {
                avatarSrc: member.avatar.defaultUrl,
                avatarAlt: member.avatar.alternativeText,
            };
        }

        return {
            avatarSrc: defaultAvatar.avatarSrc,
            avatarAlt: defaultAvatar.avatarAlt,
        };
    }

    private buildOfferItemsForMember(member: Member, contracts: Contract[]): CarrouselOfferItemViewModel[] {
        const offersItems: CarrouselOfferItemViewModel[] = [];

        if (!member?.associatedContracts?.length) {
            return offersItems;
        }

        for (const contractRef of member.associatedContracts) {
            const offerItem = this.findAndBuildOfferItem(contractRef.installedContractId, contracts);
            if (offerItem) {
                offersItems.push(offerItem);
            }
        }

        return offersItems;
    }

    private findAndBuildOfferItem(
        associatedContractId: string,
        contracts: Contract[],
    ): CarrouselOfferItemViewModel | null {
        if (!contracts?.length) {
            return null;
        }

        const contract = contracts.find(
            (c: Contract) =>
                c.cid === associatedContractId &&
                !['CLOS', 'RESILIE'].includes(c?.status) &&
                c.telco?.state !== 'VIM_ELIGIBLE',
        );

        return contract ? this.getCarrouselNavigations(contract) : null;
    }

    private getCarrouselContractItemViewModel(
        contractItem: CarrouselItem,
        contracts: Contract[],
    ): CarrouselItemViewModel {
        const carrouselItemViewModel = {} as CarrouselItemViewModel;
        carrouselItemViewModel.offerItems = [];

        if (!contracts?.length) {
            return carrouselItemViewModel;
        }

        const matchingContract = contracts.find((c: Contract) => c.cid === contractItem.contractId);
        if (!matchingContract) {
            return carrouselItemViewModel;
        }

        const contractType = this.getContractType(matchingContract);
        const { label, trackingName } = this.getContractTrackingInfo(contractType, contractItem.title);
        const { avatarSrc, avatarAlt } = this.getContractAvatarInfo(contractType);

        const offersItems = this.buildOfferItemsForContract(matchingContract);
        carrouselItemViewModel.offerItems = this.checkDisplayOffersItems(offersItems);

        if (this.shouldSetWhoUsesIt(matchingContract)) {
            carrouselItemViewModel.whoUsesIt = true;
            carrouselItemViewModel.whoUsesItUrl = this.getUrlNavigation(
                matchingContract.cid,
                this.carrouselDetailsTranslate.who_uses_it.url,
            );
        }

        carrouselItemViewModel.id = contractItem.contractId;
        carrouselItemViewModel.type = contractItem.type;
        carrouselItemViewModel.avatarAlt = avatarAlt;
        carrouselItemViewModel.avatarSrc = avatarSrc;
        carrouselItemViewModel.button = this.extractButton(contractItem.contractId, label, trackingName);

        return carrouselItemViewModel;
    }

    private getContractTrackingInfo(
        contractType: string,
        defaultLabel: string,
    ): { label: string; trackingName: TrackedNames } {
        let label = defaultLabel;
        let trackingName = TrackedNames.CONTRACT;

        if (contractType !== DEFAULT_CONTRACT) {
            trackingName = this.getTrackedNameFromValue(contractType);
        }

        if (this.carrouselTranslate[contractType]?.label) {
            label = this.carrouselTranslate[contractType].label;
        }

        return { label, trackingName };
    }

    private getContractAvatarInfo(contractType: string): { avatarSrc: string; avatarAlt: string } {
        const defaultAvatar = this.carrouselTranslate.default;

        if (this.carrouselTranslate[contractType]?.avatarSrc && this.carrouselTranslate[contractType].avatarAlt) {
            return {
                avatarSrc: this.carrouselTranslate[contractType].avatarSrc,
                avatarAlt: this.carrouselTranslate[contractType].avatarAlt,
            };
        }

        return {
            avatarSrc: defaultAvatar.avatarSrc,
            avatarAlt: defaultAvatar.avatarAlt,
        };
    }

    private shouldSetWhoUsesIt(contract: Contract): boolean {
        return contract.familyEligibility === true && contract.associatedFamilyMembers.length === 0;
    }

    private buildOfferItemsForContract(contract: Contract): CarrouselOfferItemViewModel[] {
        const offerItem = this.getCarrouselNavigations(contract);
        return [offerItem];
    }

    private extractButton(itemId: string, label: string, trackingName: TrackedNames): CarrouselItemButtonViewModel {
        const queryParams = {};
        const params = new Map<string, string>();
        if (itemId) {
            queryParams[CARROUSEL_CURRENT_ITEM_KEY] = btoa(itemId);
            params.set(CARROUSEL_CURRENT_ITEM_KEY, btoa(itemId));
        }

        return {
            label: label,
            link: this.carrouselTranslate.default.link,
            navigationExtras: {
                queryParams: queryParams,
            },
            params: params,
            trackingArea: TrackedZones.CARE_HP_CARROUSEL,
            trackingName: trackingName,
        };
    }

    private extractButtonNavigation(
        itemId: string,
        label: string,
        link: Link,
        trackingName: TrackedNames,
    ): CarrouselItemButtonViewModel {
        const queryParams = {};
        const params = new Map<string, string>();
        if (itemId) {
            queryParams[CARROUSEL_CURRENT_ITEM_KEY] = btoa(itemId);
            params.set(CARROUSEL_CURRENT_ITEM_KEY, btoa(itemId));
        }

        return {
            label: label,
            link: link,
            navigationExtras: {
                queryParams: queryParams,
            },
            params: params,
            trackingArea: TrackedZones.CARE_HP_CARROUSEL,
            trackingName: trackingName,
        };
    }

    private getCarrouselAddMemberItemViewModel(): CarrouselItemViewModel {
        const addMember = this.carrouselTranslate.addMember;

        return {
            id: `/${addMember.link.inApp}`,
            avatarSrc: addMember.avatarSrc,
            avatarAlt: addMember.avatarAlt,
            button: {
                label: addMember.label,
                link: addMember.link,
                navigationExtras: {
                    queryParams: {},
                },
                params: undefined,
                trackingArea: TrackedZones.CARE_HP_CARROUSEL,
                trackingName: TrackedNames.ADD_MEMBER,
            },
            isAddMember: addMember.isAddMember,
        };
    }

    private getCarrouselNavigations(contract?: Contract, isSharedServices?: boolean): CarrouselOfferItemViewModel {
        const carrouselOfferItemViewModel = {} as CarrouselOfferItemViewModel;
        const contractType = this.getContractType(contract);

        this.carrouselTranslateNavigation = this.translate.instant('navigations');

        this.setCarrouselOfferProperties(carrouselOfferItemViewModel, contract, isSharedServices);

        if (this.isNonConsultableContract(contractType)) {
            return carrouselOfferItemViewModel;
        }

        if (contract?.usages) {
            const carrouselNavigations = this.buildNavigationsFromUsages(
                contract,
                contractType,
                carrouselOfferItemViewModel.cid,
            );
            carrouselOfferItemViewModel.navigation = this.filterAndSortNavigations(carrouselNavigations);
        }

        this.addCybersecurityNavigationIfNeeded(contract, carrouselOfferItemViewModel);

        return carrouselOfferItemViewModel;
    }

    private setCarrouselOfferProperties(
        viewModel: CarrouselOfferItemViewModel,
        contract?: Contract,
        isSharedServices?: boolean,
    ): void {
        if (contract?.brand) {
            viewModel.brand = contract.brand;
        }
        if (contract?.cid) {
            viewModel.cid = contract.cid;
        }
        if (contract?.telco?.type) {
            viewModel.type = contract.telco.type;
        }
        if (contract?.telco?.subType) {
            viewModel.subType = contract.telco.subType;
        }
        if (isSharedServices) {
            viewModel.offerName = this.carrouselDetailsTranslate.sharedServices;
            viewModel.cid = 'sharedServices';
        }
        if (contract?.offerType) {
            viewModel.offerName = contract.offerType;
        }
        if (!contract?.security) {
            this.setLocationOrPhoneNumber(viewModel, contract);
        }
    }

    private setLocationOrPhoneNumber(viewModel: CarrouselOfferItemViewModel, contract?: Contract): void {
        if (contract?.contractInstallationArea?.city) {
            viewModel.city = contract.contractInstallationArea.city;
        } else if (!contract?.contractInstallationArea && contract?.telco?.publicNumber) {
            viewModel.phoneNumber = PhoneNumberUtil.getFormattedPhone(contract.telco.publicNumber);
        }
    }

    private buildNavigationsFromUsages(
        contract: Contract,
        contractType: string,
        cid: string,
    ): CarrouselNavigationItemViewModel[] {
        const carrouselNavigations: CarrouselNavigationItemViewModel[] = [];

        if (![MOBICARTE, OPEN_CARAIBE].includes(contractType)) {
            this.generateO2AndBillEntries(
                carrouselNavigations,
                'FACTURES',
                { cid } as CarrouselOfferItemViewModel,
                TrackedNames.FACTURES,
                1,
            );
        }

        this.processUsages(contract, carrouselNavigations, cid);

        if (![FIXE, OPEN_CARAIBE].includes(contractType)) {
            this.generateO2AndBillEntries(
                carrouselNavigations,
                'OFFRE_OPTIONS',
                { cid } as CarrouselOfferItemViewModel,
                TrackedNames.OFFRE_OPTIONS,
                2,
            );
        }

        return carrouselNavigations;
    }

    private processUsages(
        contract: Contract,
        carrouselNavigations: CarrouselNavigationItemViewModel[],
        cid: string,
    ): void {
        contract.usages?.forEach((usage: Usage) => {
            this.addNavigationItemsForUsage(contract, usage, carrouselNavigations, cid);
            this.addTVNavigationIfNeeded(contract, usage, carrouselNavigations, cid);
        });
    }

    private addNavigationItemsForUsage(
        contract: Contract,
        usage: Usage,
        carrouselNavigations: CarrouselNavigationItemViewModel[],
        cid: string,
    ): void {
        Object.keys(this.carrouselTranslateNavigation).forEach((key) => {
            if (this.shouldAddNavigationForKey(key, usage)) {
                const carrouselNavigationItemViewModel = this.addCarrouselNavigationItem(contract, key, usage, cid);
                carrouselNavigations.push(carrouselNavigationItemViewModel);
            }
        });
    }

    private shouldAddNavigationForKey(key: string, usage: Usage): boolean {
        const navigationConfig = this.carrouselTranslateNavigation[key];
        return (
            navigationConfig?.usageCode !== undefined &&
            navigationConfig.usageCode.indexOf(usage?.usageCode.slice(0, 2)) !== -1
        );
    }

    private addTVNavigationIfNeeded(
        contract: Contract,
        usage: Usage,
        carrouselNavigations: CarrouselNavigationItemViewModel[],
        cid: string,
    ): void {
        const hasTVNav = carrouselNavigations.find((nav) => nav.usageCode?.slice(0, 2) === UsageNameToCodeEnum.tv);

        if (this.hasTVOption(usage, contract) && !hasTVNav) {
            const carrouselNavigationItemViewModel = this.addCarrouselNavigationItem(contract, 'TV', usage, cid);
            carrouselNavigations.push(carrouselNavigationItemViewModel);
        }
    }

    private filterAndSortNavigations(
        carrouselNavigations: CarrouselNavigationItemViewModel[],
    ): CarrouselNavigationItemViewModel[] {
        return carrouselNavigations
            .filter((obj, index) => {
                return ['MULTISIMI', 'MULTISIMIA'].includes(obj.type) // display all multi-sim contract ecq-50093
                    ? true
                    : index === carrouselNavigations.findIndex((o) => obj.label === o.label && obj.type === o.type);
            })
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }

    private addCybersecurityNavigationIfNeeded(
        contract: Contract | undefined,
        carrouselOfferItemViewModel: CarrouselOfferItemViewModel,
    ): void {
        const hasCyberOption =
            contract?.options?.includes(OptionCode.PACK_CYBER_HOME) ||
            contract?.options?.includes(OptionCode.PACK_CYBER_MOBILE);

        if (!hasCyberOption) {
            return;
        }

        carrouselOfferItemViewModel.hasCybersecureOption = true;

        const cyberSecurityNavigationItem = this.addCarrouselNavigationItem(
            contract,
            'CYBERSECURITY',
            null,
            carrouselOfferItemViewModel.cid,
        );

        if (!carrouselOfferItemViewModel.navigation) {
            carrouselOfferItemViewModel.navigation = [];
        }
        carrouselOfferItemViewModel.navigation.push(cyberSecurityNavigationItem);
        carrouselOfferItemViewModel.navigation.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    private hasTVOption(usage: Usage, contract: Contract): boolean {
        return (
            usage.usageCode === UsageNameToCodeEnum.tv ||
            contract.options?.includes(OptionCode.SMART_TV) ||
            contract.options?.includes(OptionCode.I_TV_DIGITALE)
        );
    }

    private addCarrouselNavigationItem(
        contract: Contract,
        key: string,
        usage: Usage,
        cid: string,
    ): CarrouselNavigationItemViewModel {
        const url: Link = this.getUrlNavigation(
            contract.cid,
            this.carrouselTranslateNavigation[key].url,
            usage?.usageCode,
        );
        const label = this.getLabelForNavigationItem(key, usage);

        return new CarrouselNavigationItemViewModel({
            label,
            link: url,
            href: this.extractUrl(url),
            type: key,
            button: this.extractButtonNavigation(cid, label, url, TrackedNames[key]),
            trackingName: TrackedNames[key],
            displayOrder: DisplayOrder[key],
            usageCode: usage?.usageCode,
        });
    }

    private checkDisplayOffersItems(contractItems: CarrouselOfferItemViewModel[]): CarrouselOfferItemViewModel[] {
        if (contractItems.length === 1) {
            contractItems[0].city = null;
            contractItems[0].phoneNumber = null;
        } else {
            const mobileContracts = contractItems.filter(
                (contract) => contract.type === 'mobilePostpaid' && contract.subType === 'mobile',
            );
            if (mobileContracts.length === 1) {
                contractItems = contractItems.map((contract) =>
                    contract.cid === mobileContracts[0].cid ? { ...contract, phoneNumber: null } : contract,
                );
            }

            const openInternetContracts = contractItems.filter(
                (contract) =>
                    (contract.type === 'open' && contract.subType === 'open') ||
                    (contract.type === 'internet' && contract.subType === 'internet'),
            );
            if (openInternetContracts.length === 1) {
                contractItems = contractItems.map((contract) =>
                    contract.cid === openInternetContracts[0].cid ? { ...contract, city: null } : contract,
                );
            }
        }

        return contractItems;
    }

    private getLabelForNavigationItem(key: string, usage: Usage) {
        if (!['MULTISIMI', 'MULTISIMIA'].includes(key)) {
            return this.carrouselTranslateNavigation[key].label;
        }

        if (!usage) {
            return this.carrouselTranslateNavigation[key].label;
        }

        return usage.usageCommercialLabel;
    }

    public getContractType(contract: Contract): string {
        let contractType = DEFAULT_CONTRACT;
        if (contract) {
            if (contract.telco?.subType === 'parnasse') {
                contractType = PARNASSE_CONTRACT;
            } else if (contract.security?.version === 1) {
                contractType = PROTECTED_HOUSE_CONTRACT;
            } else if (contract.security?.version === 2) {
                contractType = PROTECTED_HOUSE_CONTRACT_2;
            } else if (contract.telco?.marketType === 'OBS') {
                contractType = ORANGE_BUSINESS_CONTRACT;
            } else if (contract.telco?.marketType === 'PRO') {
                contractType = ORANGE_PRO_CONTRACT;
            } else if (contract.isOpenCaraibe) {
                contractType = OPEN_CARAIBE;
            } else if (contract.telco?.type === 'mobilePrepaid' && contract.telco?.subType === 'mobile') {
                contractType = MOBICARTE;
            } else if (contract.telco?.type === FIXE) {
                contractType = FIXE;
            }
        }
        return contractType;
    }

    public extractLinks(isSosh: Signal<boolean>): Signal<string[]> {
        return computed(() => {
            const isSoshValue = isSosh();
            const translationKey = isSoshValue ? 'openLinkSosh' : 'openLinks';
            this.carrouselTranslatelinks = this.translate.instant(translationKey);
            return Object.keys(this.carrouselTranslatelinks).map((key) => `${translationKey}.${key}`);
        });
    }

    public getTrackedNameFromValue(contractType: string): TrackedNames {
        const trackedName = Object.values(TrackedNames).find((name) => name === contractType);

        return trackedName ?? TrackedNames.CONTRACT;
    }

    public getUrlNavigation(cid: string, navigation: string, usageCode?: string): Link {
        return Object.assign(new Link(), {
            inApp: usageCode
                ? navigation.replace('{{cid}}', cid).replace('{{usageCode}}', usageCode)
                : navigation.replace('{{cid}}', cid),
        });
    }

    public extractUrl(url: Link): string {
        return this.linkService.getLinkSync(url);
    }

    public isNonConsultableContract(contractType: string): boolean {
        return [PARNASSE_CONTRACT, ORANGE_PRO_CONTRACT, ORANGE_BUSINESS_CONTRACT].includes(contractType);
    }

    public generateO2AndBillEntries(
        carrouselNavigations: CarrouselNavigationItemViewModel[],
        entryName: string,
        carrouselOfferItemViewModel: CarrouselOfferItemViewModel,
        entry: TrackedNames,
        order: number,
    ): void {
        const url: Link = this.getUrlNavigation(
            carrouselOfferItemViewModel.cid,
            this.carrouselTranslateNavigation[entryName].url,
        );

        carrouselNavigations.push(
            Object.assign(new CarrouselNavigationItemViewModel(), {
                label: this.carrouselTranslateNavigation[entryName].label,
                link: url,
                href: this.extractUrl(url),
                type: entryName,
                button: this.extractButtonNavigation(
                    carrouselOfferItemViewModel.cid,
                    this.carrouselTranslateNavigation[entryName].label,
                    url,
                    entry,
                ),
                displayOrder: order,
            }),
        );
    }
}
